import { useTranslation } from '@framework/i18n';
import { omit } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useInputController } from '../hooks/useInputController';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';
import { useFormItemEnabled } from '../item/useFormItemEnabled';
import { AsyncSelectInputMeta, AsyncSelectMeta } from './AsyncSelectInputMeta';
import {
  AsyncSelectInputObject,
  IAsyncSelectInputObjectRef,
} from './AsyncSelectInputObject';
import { Select, SelectProps } from './select/Select';
import { SelectReadOnly } from './select/SelectReadOnly';
import { useOnSelectDropdownRefChange } from './select/useOnSelectDropdownRefChange';
import { useSelectScreenReaderLabelAddition } from './select/useSelectScreenReaderLabelAddition';
import { SelectOption } from './SelectInput';

export interface IBaseAsyncSelectInputProps
  extends Omit<SelectProps, 'name' | 'options'> {
  name: string;
  /**
   * Se specificato, viene utilizzato per creare un campo nascosto che
   * contiene l'intero oggetto (es. `DTO` ritornato dalle API) selezionato,
   * invece che solo il valore (es. `id`)
   */
  objectName?: string;
  children?: React.ReactNode;
  readOnly?: boolean;
}

export interface IAsyncSelectInputProps<
  TParams extends object,
  TItem extends object,
  TData extends IArrayResponse<TItem>,
> extends IBaseAsyncSelectInputProps {
  queryOptions: IQueryOptionsFn<TParams, TData>;
  initialParams?: NoInfer<TParams>;
  queryFn: IQueryFn<TParams, TData>;
  asOption: (data: TItem) => SelectOption;
  onAfterChange?: (value: any, objects: TItem[] | TItem | undefined) => void;
  responseMeta?: (response: TData) => AsyncSelectMeta;
  searchByField?: keyof TItem | keyof TParams;
  /**
   * Se specificato, viene utilizzato per indicare se disabilitare la query.
   * Ad esempio, per un minimo di caratteri da digitare.
   */
  skip?: (search: string) => boolean;
  /**
   * Mostra un messaggio personalizzato quando non ci sono risultati
   */
  notFoundMessage?: (search: string | undefined) => string | undefined;
  /**
   * Permette di filtrare ulteriormente le opzioni lato frontend.
   * Per esempio può essere utilizzato per nascondere delle opzioni
   * non filtrabili dalla query/API.
   */
  filter?: (options: TItem) => boolean;
}

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

export interface IPaginatedResponse<T extends object> {
  meta?: any;
  dati?: T[] | null;
}

type IArrayResponse<T extends object> = T[] | IPaginatedResponse<T>;

export type IQueryFn<TParams extends object, TData> = (
  params?: TParams,
) => Promise<TData>;

export interface AsyncSelectOption<TItem extends object> extends SelectOption {
  object: TItem;
}

// export type IGetQueryKeyFn<TParams extends object> = (params?: TParams) => readonly string[];

export type IQueryOptionsFn<TParams extends object, TData> = (
  params?: TParams,
  options?: {
    query: UseQueryOptions<TData>;
    request?: any;
  },
) => UseQueryOptions<TData>;

export function AsyncSelectInput<
  TParams extends object,
  TItem extends object,
  TData extends IArrayResponse<TItem>,
>(props: IAsyncSelectInputProps<TParams, TItem, TData>) {
  const {
    name,
    children,
    style,
    queryFn,
    initialParams,
    queryOptions,
    searchByField,
    onAfterChange,
    asOption,
    objectName,
    readOnly,
    responseMeta,
    skip,
    notFoundMessage,
    allowClear = true,
    filter,
    ...inputProps
  } = props;

  const { t } = useTranslation();
  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange: onFieldChange, onBlur, ref },
  } = useInputController(name, { enabled });

  // Query
  const [params, setParams] = useState<TParams | undefined>(initialParams);

  const isQueryEnabled = skip
    ? !skip((params as any)?.[searchByField as string] ?? '')
    : true;

  const internalQueryOptions = queryOptions(params, {
    query: {
      keepPreviousData: true,
      enabled: isQueryEnabled,
    },
  });

  const query = useQuery(internalQueryOptions) as UseQueryResult<TData> & {
    queryKey: QueryKey;
  };
  query.queryKey = internalQueryOptions.queryKey!;

  const mapToOption = useCallback(
    (value: TItem) => {
      return {
        ...asOption(value),
        object: value,
      };
    },
    [asOption],
  );

  const options = useMemo(() => {
    if (!query.data) return [];
    const data = Array.isArray(query.data) ? query.data : query.data.dati ?? [];
    const options = data
      .filter(item => (filter ? filter(item) : true))
      .map(mapToOption);

    return options;
  }, [mapToOption, query.data, filter]);

  const objectRef = useRef<IAsyncSelectInputObjectRef>(null);

  const onChange = useCallback(
    (rawValue: any) => {
      // Se il valore è undefined, allora lo imposto a null (https://react-hook-form.com/docs/useform/register)
      const value = rawValue ?? null;

      onFieldChange(value);

      if (onAfterChange != null || objectRef.current) {
        if (props.mode === 'multiple') {
          const objects = options
            .filter(o => value?.includes(o.value))
            .map(o => o.object);

          objectRef.current?.onChange(objects);
          onAfterChange?.(value, objects);
        } else {
          const object = options.find(o => o.value === value)?.object;
          objectRef.current?.onChange(object);
          onAfterChange?.(value, object);
        }
      }
    },
    [options, onAfterChange, onFieldChange, props.mode],
  );

  // Se il campo viene reimpostato, reimposto anche la ricerca
  useEffect(() => {
    if (!value) {
      setParams(initialParams);
    }
  }, [value]);

  // Aggiungo un label nascosto per l'accessibilità, che indica il valore
  // selezionato e se ci sono opzioni o meno nella Select.
  useSelectScreenReaderLabelAddition({
    value,
    options,
    mode: inputProps.mode,
    filterProp: 'label',
    readOnly,
    loading: query.isLoading || query.isFetching,
  });

  // Gestione del backspace e delete per la cancellazione del valore,
  // per supportare l'accessibilità.
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        allowClear &&
        props.mode !== 'multiple'
      ) {
        onChange(null);
      }
    },
    [allowClear, onChange],
  );

  // Nel caso cambino le options (e.g. async select) e il valore non
  // sia presente tra le opzioni disponibili, allora devo resettare il campo
  // Non possiamo usarlo per le select asincrone, perché le options
  // sono sempre parziali
  // useFilterSelectValuesOnOptionsChange({
  //   mode: props.mode,
  //   options: query.isLoading ? null : options,
  //   value,
  //   onChange,
  // });

  const accessibility = useFormItemAccessibility();
  const onDropdownRefChange = useOnSelectDropdownRefChange(inputProps.mode);

  const notFountContent = useMemo(() => {
    if (query.isError)
      return t(
        'inputs.async-select.errore-caricamento-opzioni',
        'Errore durante il caricamento dei dati',
      );
    if (query.isLoading)
      return t('inputs.async-select.caricamento-opzioni', 'Caricamento...');
    return (
      notFoundMessage?.((params as any)?.[searchByField as string] ?? '') ??
      t('inputs.async-select.nessun-risultato', 'Nessun risultato trovato')
    );
  }, [
    notFoundMessage,
    params,
    query.isError,
    query.isLoading,
    searchByField,
    t,
  ]);

  if (readOnly) {
    return (
      <SelectReadOnly
        value={value}
        options={options ?? []}
        filterProp="label"
      />
    );
  }

  return (
    <>
      {objectName && (
        <AsyncSelectInputObject name={objectName} ref={objectRef} />
      )}
      <Select
        size="large"
        style={{ textAlign: 'left', ...style }}
        disabled={enabled === false || layoutEnabled === false}
        // Le Select non supportano aria-required, errore di validazione Axe
        {...omit(accessibility.attributes, ['aria-required'])}
        allowClear={allowClear}
        showSearch
        onInputKeyDown={handleInputKeyDown}
        filterOption={!searchByField}
        optionFilterProp={searchByField ? undefined : 'label'}
        loading={query.isLoading || query.isFetching}
        onSearch={
          searchByField
            ? (value: string) => {
                setParams(
                  p =>
                    ({
                      ...p,
                      [searchByField]: value,
                    } as TParams),
                );
              }
            : undefined
        }
        notFoundContent={notFountContent}
        {...inputProps}
        options={options}
        ref={ref}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        dropdownRender={menu => (
          <div ref={onDropdownRefChange}>
            {menu}
            <AsyncSelectInputMeta
              meta={
                responseMeta && query.data
                  ? responseMeta(query.data)
                  : undefined
              }
            />
          </div>
        )}
      />
    </>
  );
}
